import { FC, ReactNode } from 'react';

import { styled } from '@mui/material/styles';
import FormGroup from '@o2/ui/FormGroup';
import IconButton, { IconButtonProps } from '@o2/ui/IconButton';
import TextField, { TextFieldProps } from '@o2/ui/TextField';

export type AddonTextFieldProps = TextFieldProps & {
  /**
   * Props passed to the IconButton.
   */
  buttonProps?: IconButtonProps;

  icon: ReactNode;
};

interface FormGroupOwnerState {
  fullWidth: TextFieldProps['fullWidth'];
}

interface IconButtonOwnerState {
  buttonColor: NonNullable<AddonTextFieldProps['color']>;
}

const NonWrappingFormGroup = styled(FormGroup)<{ ownerState: FormGroupOwnerState }>(
  ({ ownerState: { fullWidth } }) => ({
    flexWrap: 'nowrap',
    ...(fullWidth && { flexGrow: 1, width: '100%', maxWidth: 'calc(100% - 4px)' }),
  })
);

const StyledTextField = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    // fix the button being smaller than the focused input (FF, Chrome)
    paddingTop: '0.5px',
    '&.Mui-focused': {
      marginTop: '0.5px',
      paddingTop: 0,
    },
  },
}));

const StyledIconButton = styled(IconButton)<{ ownerState: IconButtonOwnerState }>(
  ({ ownerState: { buttonColor }, theme }) => ({
    background: theme.palette[buttonColor].main,
    '&:hover': {
      background: theme.palette[buttonColor].dark,
    },
    color: theme.palette[buttonColor].contrastText,
    borderRadius: 0,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    minHeight: 0,
    padding: theme.spacing(0, 1),
    // fix the button being larger than the unfocused input on FF
    marginTop: '0.05px',
    marginBottom: 0,
  })
);

/**
 * Input with an IconButton, like a InputGroup button addon in Bootstrap:
 * https://getbootstrap.com/docs/5.2/forms/input-group/#button-addons.
 *
 * @deprecated
 */
const AddonTextField: FC<AddonTextFieldProps> = ({ buttonProps, color = 'primary', icon, sx, ...props }) => {
  const { fullWidth } = props;

  return (
    <NonWrappingFormGroup row sx={sx} ownerState={{ fullWidth }} noGap>
      <StyledTextField color={color} {...props} />
      <StyledIconButton ownerState={{ buttonColor: color }} {...buttonProps}>
        {icon}
      </StyledIconButton>
    </NonWrappingFormGroup>
  );
};

export default AddonTextField;
